import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { RefreshCw, ShieldAlert } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Q7 one-press removal (director approved 15 Oct 2026): fixed list of 21 id prefixes, dry run first, typed "REMOVE 21" to confirm,
// background job through the dashboard's own soft-delete -> purge path. No address is shown anywhere.
export const Q7RemovalCard = ({ token, onDone }) => {
  const H = { headers: { Authorization: `Bearer ${token}` } };
  const [dry, setDry] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [task, setTask] = useState(null);

  const runDry = async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/cleanup/q7/dry-run`, H);
      setDry(r.data);
      setTask(null);
      toast.success(`Dry run: ${r.data.will_remove} will be removed, ${r.data.will_skip} skipped. Nothing was changed.`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Dry run failed'); } finally { setBusy(false); }
  };

  const confirm = async () => {
    if (!dry || typed.trim() !== dry.confirm_phrase) return;
    setBusy(true);
    try {
      const fingerprints = Object.fromEntries(dry.rows.map((r) => [r.id_prefix, r.fingerprint]));
      const r = await axios.post(`${API_URL}/api/admin/cleanup/q7/remove`, { confirm: typed.trim(), dry_run_token: dry.dry_run_token, fingerprints }, H);
      setTask(r.data);
      setConfirmOpen(false);
      setTyped('');
      toast.success('Removal started in the background');
    } catch (e) { toast.error(e.response?.data?.detail || 'Removal refused'); } finally { setBusy(false); }
  };

  useEffect(() => {
    if (!task || task.status !== 'running') return undefined;
    const id = setInterval(async () => {
      try {
        const r = await axios.get(`${API_URL}/api/admin/tasks/${task.id}`, H);
        setTask(r.data);
        if (r.data.status !== 'running' && onDone) onDone();
      } catch (e) { /* keep polling */ }
    }, 2000);
    return () => clearInterval(id);
  }, [task]); // eslint-disable-line react-hooks/exhaustive-deps

  const canConfirm = !!dry && dry.will_remove > 0 && !task;
  const result = task?.status === 'completed' ? task.result : null;
  return (
    <div className="rounded-2xl border border-amber-300/60 bg-amber-50/30 p-5 mb-6" data-testid="q7-removal-card">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <h3 className="font-bold flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-amber-700" /> Remove approved test accounts (Q7)</h3>
          <p className="text-xs text-muted-foreground">21 id prefixes fixed in code. Dry run first (changes nothing). Confirm runs Delete → Purge per account in the background; keepers, held and non-empty accounts are always skipped.</p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button size="sm" variant="outline" className="rounded-full" disabled={busy} onClick={runDry} data-testid="q7-dry-run-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${busy ? 'animate-spin' : ''}`} />Dry run
          </Button>
          <Button size="sm" className="rounded-full bg-red-600 hover:bg-red-700 text-white" disabled={!canConfirm || busy} onClick={() => setConfirmOpen(true)} data-testid="q7-confirm-btn">
            Confirm removal
          </Button>
        </div>
      </div>
      {dry && (
        <div data-testid="q7-dry-run-result">
          <p className="text-sm mb-2"><span className="font-semibold" data-testid="q7-will-remove">{dry.will_remove} will remove</span> · <span data-testid="q7-will-skip">{dry.will_skip} will skip</span> · backups listing: {dry.backups_listing} · checked {String(dry.checked_at).slice(0, 19)} UTC</p>
          <table className="w-full text-xs" data-testid="q7-dry-run-table">
            <thead><tr className="text-left text-muted-foreground"><th className="py-1">Id</th><th>Created</th><th>Status</th><th>Meals</th><th>Photos</th><th>Backups</th><th>Legal hold</th><th>Decision</th></tr></thead>
            <tbody>
              {dry.rows.map((r) => (
                <tr key={r.id_prefix} className="border-t" data-testid={`q7-row-${r.id_prefix}`}>
                  <td className="py-1 font-mono">{r.id_prefix}</td><td>{r.created || '—'}</td><td>{r.status}</td><td>{r.meals}</td><td>{r.photos}</td><td>{r.backups}</td>
                  <td>{r.legal_hold ? 'HELD' : 'no'}</td>
                  <td className={r.decision === 'will remove' ? 'text-red-700 font-semibold' : 'text-muted-foreground'} data-testid={`q7-decision-${r.id_prefix}`}>{r.decision}{r.skip_reason ? ` (${r.skip_reason})` : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {task && (
        <div className="mt-3 text-sm" data-testid="q7-task-status">
          <p>Job {task.id.slice(0, 8)} · <span className="font-semibold">{task.status}</span> · {task.progress?.done ?? 0}{task.progress?.total ? `/${task.progress.total}` : ''}{task.status === 'running' ? ` · ${task.progress?.note || ''}` : ''}</p>
          {result && <p data-testid="q7-task-result">Removed {result.counts?.removed} · failed {result.counts?.failed} · skipped {result.counts?.skipped}. Audit row written; run Deletion-proof on each id from the Deleted Users panel.</p>}
          {task.error && <p className="text-red-700">{task.error}</p>}
        </div>
      )}
      {confirmOpen && dry && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="q7-confirm-dialog">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-lg font-bold text-red-700 mb-2">Remove {dry.will_remove} test account(s) permanently?</h3>
            <p className="text-sm text-muted-foreground mb-3">Each account goes through the same Delete → Purge as a manual purge: account row, per-user rows, photos and backups are destroyed; an em:-only purge record stays. Accounts that changed since the dry run are skipped. Type <span className="font-mono font-semibold">{dry.confirm_phrase}</span> to continue.</p>
            <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={dry.confirm_phrase} data-testid="q7-confirm-input" />
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" className="rounded-full" onClick={() => { setConfirmOpen(false); setTyped(''); }} data-testid="q7-confirm-cancel">Cancel</Button>
              <Button className="rounded-full bg-red-600 hover:bg-red-700 text-white" disabled={typed.trim() !== dry.confirm_phrase || busy} onClick={confirm} data-testid="q7-confirm-submit">Remove now</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
